import axios, { AxiosResponse } from "axios"
import { IRefresh } from "../types/login"
import { LocalStorageKeys } from "../types/localStorage"
import { storeProfileId } from "../helper/profileId.helper"

const $api = axios.create({
  withCredentials: true,
  baseURL: process.env.REACT_APP_API_BASE,
})

$api.interceptors.request.use((config) => {
  const token = localStorage.getItem(LocalStorageKeys.SELLER_TOKEN)
  const countryCode = localStorage.getItem(LocalStorageKeys.COUNTRY_CODE)

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  const method = (config.method || "get").toLowerCase()
  if (["post", "put", "patch"].includes(method)) {
    config.headers["Content-Type"] = "application/json"
  } else {
    delete (config.headers as any)["Content-Type"]
  }
  config.headers["countryCode"] = countryCode
  return config
})

const REFRESH_URL = "/customer/refresh"
const LOGIN_PATH = "/login"

let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []
let refreshFailureSubscribers: ((error: unknown) => void)[] = []

const subscribeOnRefresh = (
  onSuccess: (token: string) => void,
  onFailure: (error: unknown) => void
) => {
  refreshSubscribers.push(onSuccess)
  refreshFailureSubscribers.push(onFailure)
}

const notifyRefreshSuccess = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token))
  refreshSubscribers = []
  refreshFailureSubscribers = []
}

const notifyRefreshFailure = (error: unknown) => {
  refreshFailureSubscribers.forEach((callback) => callback(error))
  refreshSubscribers = []
  refreshFailureSubscribers = []
}

const redirectToLogin = () => {
  localStorage.removeItem(LocalStorageKeys.SELLER_TOKEN)
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.href = LOGIN_PATH
  }
}

const isRefreshRequest = (config: { url?: string }) =>
  Boolean(config.url && config.url.includes(REFRESH_URL))

const retryWithToken = (originalRequest: any, token: string) => {
  originalRequest.headers["Authorization"] = `Bearer ${token}`
  return $api(originalRequest)
}

const waitForTokenAndRetry = (originalRequest: any) =>
  new Promise((resolve, reject) => {
    subscribeOnRefresh(
      (newToken) => resolve(retryWithToken(originalRequest, newToken)),
      (error) => reject(error)
    )
  })

const performTokenRefresh = async () => {
  const tokenData = await $api.get<IRefresh, AxiosResponse<IRefresh>>(REFRESH_URL)
  const newAccessToken = tokenData.data.accessToken
  localStorage.setItem(LocalStorageKeys.SELLER_TOKEN, newAccessToken)
  storeProfileId(tokenData.data.profileId)
  return newAccessToken
}

$api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error)
    }

    // If the refresh endpoint itself returns 401, the session is dead — no point retrying.
    if (isRefreshRequest(originalRequest)) {
      notifyRefreshFailure(error)
      redirectToLogin()
      return Promise.reject(error)
    }

    originalRequest._retry = true

    if (isRefreshing) {
      return waitForTokenAndRetry(originalRequest)
    }

    isRefreshing = true

    try {
      const newAccessToken = await performTokenRefresh()
      notifyRefreshSuccess(newAccessToken)
      return retryWithToken(originalRequest, newAccessToken)
    } catch (e) {
      console.error("Error updating token:", e)
      notifyRefreshFailure(e)
      redirectToLogin()
      return Promise.reject(e)
    } finally {
      isRefreshing = false
    }
  }
)

export default $api
